import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/config';
import { APP_CONFIG, HTTP_STATUS } from '@/constants/app.constants';
import { tokenStorage } from '@/utils/token-storage.util';
import { localeStorage } from '@/utils/locale-storage.util';
import { isTokenExpired } from '@/utils/jwt.util';
import { authSessionEvents } from './auth-session-events';
import { tokenRefreshQueue } from './request-queue';
import { AUTH_ENDPOINTS } from './endpoints';

declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _retry?: boolean;
  }
}

export const axiosInstance = axios.create({
  baseURL: env.api.baseUrl,
  timeout: env.api.timeoutMs,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const accessToken = tokenStorage.getAccessToken();

    if (accessToken) {
      config.headers.set('Authorization', `Bearer ${accessToken}`);
    }

    const locale = localeStorage.get() ?? APP_CONFIG.DEFAULT_LOCALE;

    // `X-Locale` drives the backend's response localization (success/error messages,
    // validation errors, enum labels — see SetLocaleFromHeader middleware). The `lang`
    // query param is a separate, older mechanism used only by the Configuration domain
    // (see IndexConfigurationRequest / ConfigurationResource) and is kept as-is here.
    config.headers.set('X-Locale', locale);
    config.params = { lang: locale, ...config.params };

    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

async function refreshAccessToken(): Promise<string> {
  const refreshToken = tokenStorage.getRefreshToken();

  if (!refreshToken) {
    throw new Error('No refresh token available.');
  }

  const response = await axios.post<{ accessToken: string; refreshToken?: string }>(
    `${env.api.baseUrl}${AUTH_ENDPOINTS.REFRESH_TOKEN}`,
    { refreshToken },
  );

  tokenStorage.setAccessToken(response.data.accessToken);

  if (response.data.refreshToken) {
    tokenStorage.setRefreshToken(response.data.refreshToken);
  }

  return response.data.accessToken;
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (status === HTTP_STATUS.UNAUTHORIZED && originalRequest && !originalRequest._retry) {
      if (!env.auth.refreshTokenEnabled) {
        authSessionEvents.emitUnauthorized();
        return Promise.reject(error);
      }

      const currentAccessToken = tokenStorage.getAccessToken();

      if (currentAccessToken && !isTokenExpired(currentAccessToken)) {
        authSessionEvents.emitUnauthorized();
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (tokenRefreshQueue.isRefreshing()) {
        return new Promise((resolve, reject) => {
          tokenRefreshQueue.subscribe((newToken) => {
            originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
            resolve(axiosInstance(originalRequest));
          });
          setTimeout(() => reject(error), env.api.timeoutMs);
        });
      }

      tokenRefreshQueue.start();

      try {
        const newAccessToken = await refreshAccessToken();
        tokenRefreshQueue.resolveAll(newAccessToken);
        originalRequest.headers.set('Authorization', `Bearer ${newAccessToken}`);
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        tokenRefreshQueue.rejectAll();
        tokenStorage.clearAll();
        authSessionEvents.emitUnauthorized();
        return Promise.reject(refreshError);
      }
    }

    if (status === HTTP_STATUS.FORBIDDEN) {
      authSessionEvents.emitForbidden();
    }

    return Promise.reject(error);
  },
);
